export function describePayInType (payIn, me) {
  function type () {
    switch (payIn.payInType) {
      case 'ITEM_CREATE':
        if (!payIn.item) {
          return 'item'
        } else if (payIn.item.isJob) {
          return 'job'
        } else if (payIn.item.title) {
          return 'post'
        } else if (payIn.item.parentId) {
          return 'comment'
        } else {
          return 'item'
        }
      case 'ITEM_UPDATE':
        if (!payIn.item) {
          return 'item edit'
        } else if (payIn.item.isJob) {
          return 'job edit'
        } else if (payIn.item.title) {
          return 'post edit'
        } else if (payIn.item.parentId) {
          return 'comment edit'
        } else {
          return 'item edit'
        }
      case 'TIP':
        return 'tip'
      case 'BOUNTY_PAYMENT':
        return 'pay bounty'
      default:
        return payTypeShortName(payIn.payInType)
    }
  }

  const t = type()
  if (!payIn.isSend) {
    if (payIn.payInState === 'FAILED') {
      return t + ' refund'
    } else if (['AUTO_WITHDRAWAL', 'WITHDRAWAL'].includes(payIn.payInType)) {
      return t + ' completed'
    }
  }
  if (!payIn.payerPrivates?.userId || !payIn.isSend) {
    return t + ' receive'
  }
  if (payIn.genesisId) {
    return t + ' (retry)'
  }

  return t
}

export function payTypeShortName (type) {
  return type.toLowerCase().replaceAll('_', ' ')
}

export const FAILED_PAY_IN_STATES = ['FAILED', 'CANCELLED', 'FAILED_FORWARD']

// A successful low-rep ITEM_CREATE carries a monero: URI for the posting fee.
// The item is created immediately (PENDING_FEE, invisible until the
// rewardsWalletObserver observes the fee), so the submit flow must reset the form and
// show the payment modal instead of redirecting — otherwise the user can
// re-submit the same post and mint a second posting fee.
export const isPostingFeeSubmit = response => !!response?.moneroUri

// "paid at" is only meaningful once a fee-gated item is actually live: the
// ITEM_CREATE payIn is born PAID (mcost=0) the moment the item is created, so
// the payIn state alone cannot distinguish an unpaid PENDING_FEE post from a
// live one. Fee-exempt items (FEE_NOT_REQUIRED) keep the legacy display.
export const shouldShowItemPaidAt = (item) =>
  !!item?.payIn && item.payIn.payInState === 'PAID' && item.feeStatus !== 'PENDING_FEE'

export const isPendingFeeItem = (item) => item?.feeStatus === 'PENDING_FEE'

// Modal phase for a fee-gated item/turf. 'paid' = live; 'detected' = the full
// fee is OBSERVED on-chain but not yet chain-verified enough to flip (the
// pending-fee modal must show "waiting for confirmation" instead of re-quoting
// the fee — the re-quote bug: itemFeeReentryFunding used to hand back the full
// amount once received >= expected, which read as "pay again"); 'waiting' =
// still owed. `received`/`expected` are optional BigInts.
export const postingFeeModalPhase = (feeStatus, received = null, expected = null) => {
  if (feeStatus === 'FEE_PAID') return 'paid'
  if (received != null && expected != null && expected > 0n && received >= expected) return 'detected'
  return 'waiting'
}

// Re-entry quote math shared by itemFeeReentryFunding and
// territoryReentryFunding: nothing owed once received covers expected — the
// caller must NOT re-quote (and must not fall through to a fresh mint: that
// would strand the paid partials on an orphaned address).
export const reentryQuote = (expected, received) => {
  const remaining = expected - received
  return { remaining, fullyPaid: remaining <= 0n, amount: remaining > 0n ? remaining : expected }
}

// the default "payment settled" predicate
export const paidWaitFor = payIn => payIn?.payInState === 'PAID'

// The modal poll hooks (tip/downvote) treat an observed on-chain payment as
// "detected" at DETECTED (0-conf) AND CONFIRMED (N-conf). DETECTED is the usual
// success trigger; CONFIRMED must also trigger it because a tip/downvote that
// is already confirmed by the time the modal first polls (or that confirms
// between polls) would otherwise be a silent stop-polling terminal — leaving the
// modal stuck with no success message and no close. REORGED/EXPIRED are failure
// terminals and must NOT trigger success.
export function shouldTriggerPaymentSuccess (state) {
  return state === 'DETECTED' || state === 'CONFIRMED'
}

// acts settle 100% P2P in monero — there is no send wallet and no (wrapped) invoice to forward, so
// an act waits for its payIn to settle at PAID
export const actWaitFor = () => payIn =>
  ['FORWARDING', 'PAID'].includes(payIn?.payInState)

// the unique mutation result in a payIn mutation response (every doc returns a single root field)
export const getPayIn = data => (data ? Object.values(data)[0] : null)

export const INVOICE_SETUP_PENDING_STATES = ['PENDING_INVOICE_CREATION', 'PENDING_INVOICE_WRAP']

// A payIn in an invoice-setup state means its (legacy Bolt11) invoice creation/wrap failed: it isn't
// payable right now and is being driven to FAILED + background auto-retry. It is neither an error
// to surface nor a state to pay — the optimistic view persists while we retry.
export function isInvoiceSetupPending (payIn) {
  return INVOICE_SETUP_PENDING_STATES.includes(payIn?.payInState)
}

// force a payIn into the FAILED shape cache writes expect, preserving any failure reason
// the server already set and falling back to the given reason
export function toFailedPayIn (payIn, payInFailureReason) {
  return {
    __typename: 'PayIn',
    ...payIn,
    payInState: 'FAILED',
    payInStateChangedAt: new Date().toISOString(),
    payerPrivates: {
      __typename: 'PayerPrivates',
      ...(payIn?.payerPrivates ?? {}),
      payInFailureReason: payIn?.payerPrivates?.payInFailureReason ?? payInFailureReason
    }
  }
}

export const PAY_IN_RECEIVER_FAILURE_REASONS = [
  'INVOICE_WRAPPING_FAILED_HIGH_PREDICTED_FEE',
  'INVOICE_WRAPPING_FAILED_HIGH_PREDICTED_EXPIRY',
  'INVOICE_WRAPPING_FAILED_UNKNOWN',
  'INVOICE_FORWARDING_CLTV_DELTA_TOO_LOW',
  'INVOICE_FORWARDING_FAILED'
]

const DEFAULT_PAY_IN_FAILURE_DETAIL = {
  level: 'error',
  logMessage: 'the payment failed',
  userMessage: 'The payment failed'
}

const PAY_IN_FAILURE_DETAILS = {
  USER_CANCELLED: {
    level: 'info',
    logMessage: 'invoice canceled by payer',
    userMessage: 'Canceled by payer'
  },
  SYSTEM_CANCELLED: {
    level: 'warn',
    logMessage: 'payment canceled by system',
    userMessage: 'Canceled by system'
  },
  INVOICE_EXPIRED: {
    level: 'warn',
    logMessage: 'invoice expired before it was paid',
    userMessage: 'The invoice expired before it was paid'
  },
  WITHDRAWAL_FAILED: {
    level: 'error',
    logMessage: 'couldn\'t send the payment to the destination wallet',
    userMessage: 'We couldn\'t send the payment to the destination wallet'
  },
  INVOICE_FORWARDING_FAILED: {
    level: 'error',
    logMessage: 'couldn\'t forward the payment to the destination wallet',
    userMessage: 'We couldn\'t forward the payment to the destination wallet'
  },
  INVOICE_FORWARDING_CLTV_DELTA_TOO_LOW: {
    level: 'error',
    logMessage: 'the destination invoice did not leave enough time for the payment to arrive',
    userMessage: 'The destination invoice did not leave enough time for the payment to arrive'
  },
  INVOICE_WRAPPING_FAILED_HIGH_PREDICTED_FEE: {
    level: 'error',
    logMessage: 'network fees were too high to send this payment',
    userMessage: 'Network fees were too high for this payment'
  },
  INVOICE_WRAPPING_FAILED_HIGH_PREDICTED_EXPIRY: {
    level: 'error',
    logMessage: 'this payment would have taken too long to route',
    userMessage: 'This payment would have taken too long to route'
  },
  INVOICE_WRAPPING_FAILED_UNKNOWN: {
    level: 'error',
    logMessage: 'couldn\'t prepare the payment for the destination wallet',
    userMessage: 'We couldn\'t prepare the payment for the destination wallet'
  },
  INVOICE_CREATION_FAILED: {
    level: 'error',
    logMessage: 'couldn\'t create the invoice',
    userMessage: 'We couldn\'t create the invoice'
  },
  HELD_INVOICE_UNEXPECTED_ERROR: {
    level: 'error',
    logMessage: 'the incoming payment hit an unexpected error',
    userMessage: 'The incoming payment hit an unexpected error'
  },
  HELD_INVOICE_SETTLED_TOO_SLOW: {
    level: 'warn',
    logMessage: 'the incoming payment took too long to settle',
    userMessage: 'The incoming payment took too long to settle'
  },
  EXECUTION_FAILED: {
    level: 'error',
    logMessage: 'the payment failed unexpectedly',
    userMessage: 'The payment failed unexpectedly'
  },
  UNKNOWN_FAILURE: {
    level: 'error',
    logMessage: 'the payment failed for an unknown reason',
    userMessage: 'The payment failed for an unknown reason'
  }
}

export function getPayInFailureDetail (reason) {
  return PAY_IN_FAILURE_DETAILS[reason] ?? DEFAULT_PAY_IN_FAILURE_DETAIL
}

function normalizePayInFailureInput (input = {}) {
  if (!input || typeof input !== 'object') {
    return {}
  }

  return {
    payInFailureReason: input.payInFailureReason ?? input.payerPrivates?.payInFailureReason
  }
}

export function getPayInFailurePresentation (input) {
  const { payInFailureReason } = normalizePayInFailureInput(input)
  if (!payInFailureReason) {
    return null
  }

  const failure = getPayInFailureDetail(payInFailureReason)

  return {
    level: failure.level,
    logMessage: failure.logMessage,
    summary: failure.userMessage,
    detail: null
  }
}
